import {
  EventSetupState,
  EventStatus,
  initialEventSetupState,
} from '@deltager/organizers/admin/modules/events/create-event/store/event-setup.state';
import { Event } from '@deltager/organizers/admin/modules/events/create-event/model/event.model';
import { EventLocation } from '@deltager/organizers/admin/modules/events/create-event/model/location.model';
import { Price } from '@deltager/organizers/admin/modules/events/create-event/model/price.model';
import { Attribute } from '@deltager/organizers/admin/modules/events/create-event/model/attribute.model';
import { AttributeOption } from '@deltager/organizers/admin/modules/events/create-event/model/attribute-option.model';
import { AttributeRelation } from '@deltager/organizers/admin/modules/events/create-event/model/attribute-relation.model';
import { BuiltinAttribute } from '../model/builtin-attributes.model';
import { VatType } from '@deltager/organizers/admin/modules/events/create-event/model/vat-type.enum';
import { EventSetupWizardStep } from '@deltager/organizers/admin/modules/events/create-event/model/event-setup-wizard-step.enum';
import { Action } from '@ngrx/store';
import {
  ChangeEventStatusAction,
  ChangeEventStepAction,
  CreateEventSuccessAction,
  EditEventAction,
  EventActionTypes,
  LoadEventSuccessAction,
  PublishEventSuccessAction,
  UpdateAccountingSystemAction,
  OpenRepeatingEventPopupAction,
  EventErrorAction,
  SetLastBankAccountIdAction,
  LoadEventHeaderSuccessAction,
  SetMasterTotalSubEventsAction,
} from './event-setup.actions';
import {
  ClearFieldInStoragesAction,
  GetSessionAction,
  GetSessionNamesSuccessAction,
  LoadEventSessionsSuccessAction,
  SessionsActionTypes,
} from '@deltager/organizers/admin/modules/event-sessions/store/sessions.actions';
import { EventHeader } from '../model/event-header';
import { undefinedPropsToNull } from '@deltager/organizers/admin/shared/utils/object.utils';
import _ from 'lodash';

function initEvent(createEvent: Event): Event {
  const newEventCreated = Object.assign(
    new Event(),
    createEvent,
    {
      prices: createEvent.prices.map(v =>
        Object.assign(
          new Price(),
          v,
          {
            availableFrom: v.availableFrom ? new Date(v.availableFrom) : v.availableFrom,
          },
          {
            availableTill: v.availableTill ? new Date(v.availableTill) : v.availableTill,
          },
        ),
      ),
    },
    {
      builtinAttributes: createEvent.builtinAttributes.map(v => Object.assign(new BuiltinAttribute(), v)),
      attributes: createEvent.attributes.map(v =>
        Object.assign(
          new Attribute(),
          v,
          {
            options: v.options.map(o => Object.assign(new AttributeOption(), o)),
          },
          {
            dependencies: v.dependencies.map(d => Object.assign(new AttributeRelation(), d)),
          },
        ),
      ),
      location: new EventLocation(createEvent.location),
    },
  );

  newEventCreated.prices.forEach((v, i) => (v.uiId = i));
  newEventCreated.attributes.forEach((v, i) => (v.uiId = i));
  newEventCreated.builtinAttributes.forEach((v, i) => (v.uiId = i + 10000));
  newEventCreated.attributes.map(item => {
    item.options.forEach((v, i) => (v.uiId = i + 1));
    return item;
  });
  newEventCreated.attributes.map(item => {
    item.dependencies.forEach((v, i) => (v.uiId = i + 1));
    return item;
  });

  newEventCreated.startDate = newEventCreated.startDate
    ? new Date(newEventCreated.startDate)
    : newEventCreated.startDate;
  newEventCreated.endDate = newEventCreated.endDate ? new Date(newEventCreated.endDate) : newEventCreated.endDate;
  newEventCreated.registrationStartDate = newEventCreated.registrationStartDate
    ? new Date(newEventCreated.registrationStartDate)
    : newEventCreated.registrationStartDate;
  newEventCreated.registrationEndDate = newEventCreated.registrationEndDate
    ? new Date(newEventCreated.registrationEndDate)
    : newEventCreated.registrationEndDate;

  return newEventCreated;
}

function processInitialEvent(initialEvent: Event): Event {
  const copy = { ...initialEvent } as Event;

  return copy;
}

export const eventSetupReducer = (state: EventSetupState = initialEventSetupState, action: Action): EventSetupState => {
  switch (action.type) {
    case EventActionTypes.ChangeEventStatus: {
      const status = (action as ChangeEventStatusAction).payload.status;
      return {
        ...state,
        currentEventStatus: status,
      };
    }
    case EventActionTypes.ChangeEventStep: {
      const step = (action as ChangeEventStepAction).payload.step;

      return {
        ...state,
        currentStep: step,
      };
    }

    case EventActionTypes.ConstructEventSuccess:
    case EventActionTypes.LoadEventSuccess: {
      const loadEvent = (action as any).payload.event;
      const updatedLoadEvent = processInitialEvent(loadEvent);
      const newLoadEvent = initEvent(updatedLoadEvent);

      return Object.assign({}, state, {
        initialEvent: updatedLoadEvent,
        createdEvent: newLoadEvent,
        currentEventStatus: EventStatus.initialized,
        existingEvent: updatedLoadEvent.id > 0,
        currentStep:
          action.type === EventActionTypes.ConstructEventSuccess && updatedLoadEvent.published
            ? EventSetupWizardStep.EventDetails
            : (newLoadEvent.eventSetupStep ?? EventSetupWizardStep.EventDetails),
        eventStats:
          action.type === EventActionTypes.LoadEventSuccess
            ? (action as LoadEventSuccessAction).payload.eventStats
            : null,
        error: null,
      });
    }
    case EventActionTypes.EditEvent: {
      const editedEvent = (action as EditEventAction).payload.event;

      const adjustedEvent = undefinedPropsToNull(_.cloneDeep(editedEvent)) as Event;
      adjustedEvent.prices?.forEach(p => undefinedPropsToNull(p));

      const updatedEvent = Object.assign(new Event(), state.createdEvent, adjustedEvent);

      return {
        ...state,
        createdEvent: updatedEvent,
        currentEventStatus: EventStatus.edited,
      };
    }
    case SessionsActionTypes.LoadEventSessionsSuccess: {
      const sessionsInfo = (action as LoadEventSessionsSuccessAction).payload;
      return {
        ...state,
        eventSessions: sessionsInfo.dateSessions,
        totalAmountOfSessions: sessionsInfo.totalAmountOfSessions,
        editedSession: null,
      };
    }
    case SessionsActionTypes.GetSessionSuccess: {
      const session = (action as GetSessionAction).payload.session;

      return {
        ...state,
        editedSession: session,
      };
    }
    case SessionsActionTypes.GetSessionsNamesSuccess: {
      const sessionsList = (action as GetSessionNamesSuccessAction).payload.sessionsList;

      return {
        ...state,
        sessionNames: sessionsList,
      };
    }
    case SessionsActionTypes.ClearFieldInStorage: {
      const propertyNames = (action as ClearFieldInStoragesAction).payload.propertyNames;

      const s = { ...state };

      propertyNames.forEach(p => (s[p] = null));
      return { ...s };
    }
    case EventActionTypes.CreateEventSuccess:
    case EventActionTypes.UpdateEventSuccess: {
      const savedEvent = (action as CreateEventSuccessAction).payload.event;
      const updatedSavedEvent = processInitialEvent(savedEvent);
      const newEvent = initEvent(updatedSavedEvent);
      newEvent.editable = savedEvent.editable;

      let header = {
        ...state.eventStats,
        title: newEvent.title,
        url: newEvent.customRelativeUrl,
      } as EventHeader;

      return Object.assign({}, state, {
        initialEvent: updatedSavedEvent,
        createdEvent: newEvent,
        eventStats: header,
        error: null,
      });
    }
    case EventActionTypes.PublishEventSuccess: {
      const publishedEvent = (action as PublishEventSuccessAction).payload.event;
      const updatedSavedEvent = processInitialEvent(publishedEvent);
      const newEvent = initEvent(updatedSavedEvent);

      return Object.assign(
        {},
        state,
        {
          createdEvent: Object.assign(new Event(), newEvent, {
            published: true,
          }),
        },
        {
          currentEventStatus: EventStatus.submitted,
          savedSetupStep: EventSetupWizardStep.Unspecified,
          existingEvent: true,
        },
      );
    }
    case EventActionTypes.MarkEventAsFree: {
      return {
        ...state,
        createdEvent: Object.assign(new Event(), state.createdEvent, {
          payable: false,
          bankAccountId: 0,
          vatAvailability: VatType.NO_VAT,
          prices: state.createdEvent.prices.map(p => Object.assign(new Price(), p, { value: 0, vat: 0 })),
          attributes: state.createdEvent.attributes.map(a =>
            Object.assign(new Attribute(), a, {
              checkboxPrice: 0,
              vat: 0,
              options: a.options.map(ao =>
                Object.assign(new AttributeOption(), ao, {
                  price: 0,
                }),
              ),
            }),
          ),
        }),
      };
    }
    case EventActionTypes.MarkEventAsPaid: {
      return {
        ...state,
        createdEvent: Object.assign(new Event(), state.createdEvent, {
          payable: true,
        }),
      };
    }
    case EventActionTypes.DisableVat: {
      return {
        ...state,
        createdEvent: Object.assign(new Event(), state.createdEvent, {
          vatAvailability: VatType.NO_VAT,
        }),
      };
    }
    case EventActionTypes.UpdateAccountingSystem: {
      const accountingSystem = (action as UpdateAccountingSystemAction).accountingSystem;
      return {
        ...state,
        accountingSystem,
      };
    }
    case EventActionTypes.Error: {
      return {
        ...state,
        error: (action as EventErrorAction).payload,
        currentEventStatus: EventStatus.initialized,
      };
    }
    case EventActionTypes.OpenRepeatingEventPopup: {
      const eventId = (action as OpenRepeatingEventPopupAction).eventId;
      return {
        ...state,
        popupRepeatingEventId: eventId,
      };
    }
    case EventActionTypes.CloseRepeatingEventPopup: {
      return {
        ...state,
        popupRepeatingEventId: null,
      };
    }
    case EventActionTypes.SetLastBankAccountId: {
      const lastBankAccountId = (action as SetLastBankAccountIdAction).lastBankAccountId;
      return {
        ...state,
        lastBankAccountId,
      };
    }
    case EventActionTypes.ClearEventStore: {
      return initialEventSetupState;
    }
    case EventActionTypes.LoadEventHeaderSuccess: {
      const header = (action as LoadEventHeaderSuccessAction).payload.eventHeader;

      return Object.assign({}, state, {
        eventStats: header,
      });
    }
    case EventActionTypes.SetMasterTotalSubEvents: {
      const totalSubEvents = (action as SetMasterTotalSubEventsAction).totalSubEvents;
      return {
        ...state,
        masterTotalSubEventsCount: totalSubEvents,
      };
    }
    default:
      return state;
  }
};
